import { useEffect, useRef, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Upload, X, AlertTriangle, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { authedFetch } from "@/lib/authed-fetch";
import { previewBroadcastAudience, sendBroadcastPush } from "@/lib/push.functions";

export const Route = createFileRoute("/_authenticated/admin/broadcasts/new")({
  component: NewBroadcastPage,
});

type Audience = "all" | "paid" | "free";

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

function NewBroadcastPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [url, setUrl] = useState("/dashboard");
  const [audience, setAudience] = useState<Audience>("all");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const urlLooksAbsolute = /^https?:\/\//i.test(url.trim());

  const audienceQ = useQuery({
    queryKey: ["broadcast-audience-count", audience],
    queryFn: () => previewBroadcastAudience({ data: { audience }, fetch: authedFetch }),
    staleTime: 30_000,
  });

  // Default the URL placeholder hint
  useEffect(() => {
    if (urlLooksAbsolute) {
      // Just a soft hint; don't auto-rewrite.
    }
  }, [urlLooksAbsolute]);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick a JPG or PNG image.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be 2 MB or smaller.");
      return;
    }
    setUploading(true);
    try {
      const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("push-images")
        .upload(path, file, { contentType: file.type, upsert: false });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("push-images").getPublicUrl(path);
      setImageUrl(pub.publicUrl);
      toast.success("Image uploaded.");
    } catch (e) {
      console.error(e);
      toast.error((e as Error).message || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  const sendM = useMutation({
    mutationFn: async () => {
      return sendBroadcastPush({
        data: { title, body, url, imageUrl, audience },
        fetch: authedFetch,
      });
    },
    onSuccess: (res) => {
      toast.success(`Broadcast sent to ${res.sent} device${res.sent === 1 ? "" : "s"}.`);
      qc.invalidateQueries({ queryKey: ["push-broadcasts"] });
      navigate({ to: "/admin/broadcasts" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const canSubmit =
    title.trim().length > 0 && body.trim().length > 0 && url.trim().length > 0 && !uploading;

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/broadcasts">
            <ArrowLeft className="h-4 w-4" /> Back to broadcasts
          </Link>
        </Button>
        <h1 className="mt-2 font-display text-3xl font-bold">New broadcast</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Sends a one-off push to subscribed users. Use for announcements, offers, or articles.
        </p>
      </div>

      <div className="space-y-6 rounded-xl border border-border/60 bg-card p-6">
        {/* Title */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="bc-title">Title</Label>
            <span className="text-xs text-muted-foreground">{title.length} / 60</span>
          </div>
          <Input
            id="bc-title"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 60))}
            placeholder="e.g. National Day Offer: 30% off Pro"
          />
        </div>

        {/* Body */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="bc-body">Body</Label>
            <span className="text-xs text-muted-foreground">{body.length} / 140</span>
          </div>
          <Textarea
            id="bc-body"
            rows={3}
            value={body}
            onChange={(e) => setBody(e.target.value.slice(0, 140))}
            placeholder="Short message. Keep it scannable. iPhone shows roughly 120 characters."
          />
        </div>

        {/* URL */}
        <div className="space-y-2">
          <Label htmlFor="bc-url">Destination link</Label>
          <Input
            id="bc-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="/dashboard"
          />
          <p className="text-xs text-muted-foreground">
            Use a relative path like <code className="rounded bg-muted px-1">/dashboard</code> or{" "}
            <code className="rounded bg-muted px-1">/education/some-article</code>. Absolute URLs (https://...) can
            kick iPhone PWA users into Safari.
          </p>
          {urlLooksAbsolute && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-200">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              <span>This looks like an absolute URL. Consider using a relative path so the PWA stays in-app.</span>
            </div>
          )}
        </div>

        {/* Image */}
        <div className="space-y-2">
          <Label>Image (optional)</Label>
          {imageUrl ? (
            <div className="relative inline-block">
              <img
                src={imageUrl}
                alt="Broadcast preview"
                className="max-h-48 rounded-md border border-border/60 object-contain"
              />
              <button
                type="button"
                onClick={() => setImageUrl(null)}
                className="absolute right-2 top-2 rounded-full bg-background/90 p-1 text-foreground hover:bg-background"
                aria-label="Remove image"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-border/60 bg-background/40 px-4 py-8 text-sm text-muted-foreground hover:border-gold/60 hover:text-foreground disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Uploading.
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" /> Click to upload JPG or PNG (max 2 MB)
                </>
              )}
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFile(f);
              e.target.value = "";
            }}
          />
          <p className="text-xs text-muted-foreground">
            Recommended size: 1200 x 630 pixels (1.91:1 ratio). Shown as a large banner on Android. iPhone shows
            title and body only.
          </p>
        </div>

        {/* Audience */}
        <div className="space-y-2">
          <Label>Audience</Label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {(
              [
                { key: "all", label: "All subscribers", desc: "Everyone with push enabled" },
                { key: "paid", label: "Paid users", desc: "Standard, Pro, Elite, Admin" },
                { key: "free", label: "Free users", desc: "No active subscription" },
              ] as Array<{ key: Audience; label: string; desc: string }>
            ).map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setAudience(opt.key)}
                className={`rounded-md border p-3 text-left text-sm transition ${
                  audience === opt.key
                    ? "border-gold bg-gold-muted/30 text-foreground"
                    : "border-border/60 text-muted-foreground hover:border-border"
                }`}
              >
                <div className="font-display font-bold">{opt.label}</div>
                <div className="text-xs text-muted-foreground">{opt.desc}</div>
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Estimated reach:{" "}
            {audienceQ.isLoading
              ? "calculating."
              : audienceQ.data
                ? `${audienceQ.data.users} user${audienceQ.data.users === 1 ? "" : "s"} (${audienceQ.data.subscriptions} device${audienceQ.data.subscriptions === 1 ? "" : "s"})`
                : "unavailable"}
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t border-border/60 pt-4">
          <Button variant="outline" asChild>
            <Link to="/admin/broadcasts">Cancel</Link>
          </Button>
          <Button
            disabled={!canSubmit || sendM.isPending}
            onClick={() => setConfirmOpen(true)}
          >
            <Send className="h-4 w-4" /> Send broadcast
          </Button>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send this broadcast?</DialogTitle>
            <DialogDescription>
              You are about to push this notification to{" "}
              {audienceQ.data
                ? `${audienceQ.data.subscriptions} device${audienceQ.data.subscriptions === 1 ? "" : "s"} (${audienceQ.data.users} user${audienceQ.data.users === 1 ? "" : "s"})`
                : "the selected audience"}
              . This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-border/60 bg-background/40 p-3 text-sm">
            <div className="font-display font-bold">{title || "(no title)"}</div>
            <div className="text-muted-foreground">{body || "(no body)"}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={sendM.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmOpen(false);
                sendM.mutate();
              }}
              disabled={sendM.isPending}
            >
              {sendM.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Confirm and send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
